<template>
  <div class="publishing-properties">
    <ConfigList>
      <ul>
        <li>
          <div>
            <p>
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.PRODUCTS_CREATED.HEADING') }}
            </p>
            <p class="text-sm text-grey">
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.PRODUCTS_CREATED.SUBHEADING') }}
            </p>
          </div>
          <div>
            <IncrementCounter
              v-if="showProgressBar"
              :key="updateTimestamp"
              :to="composition.sellables.length"
              :time="1500"
            />
            <span v-else>{{ composition.sellables.length }}</span>
          </div>
        </li>
        <li>
          <div>
            <p>
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_CATEGORY.HEADING') }}
            </p>
            <p class="text-sm text-grey">
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_CATEGORY.SUBHEADING') }}
            </p>
          </div>
          <div
            v-if="
              selectedAssortmentCategoryNames.length >=
              assortmentCategories.length
            "
          >
            {{ $t('GENERAL.ALL') }}
          </div>
          <div
            v-else
            v-tooltip="{
              content: selectedAssortmentCategoryNames.join(', '),
            }"
          >
            {{ selectedAssortmentCategoryNames.length }} /
            {{ assortmentCategories.length }}
          </div>
        </li>
        <li v-if="hasApparelCategory">
          <div>
            <p>
              {{
                $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_TARGET_GROUPS.HEADING')
              }}
            </p>
            <p class="text-sm text-grey">
              {{
                $t(
                  'PUBLISHING.DETAIL.PRODUCTS.DEFAULT_TARGET_GROUPS.SUBHEADING',
                )
              }}
            </p>
          </div>
          <div v-if="selectedLabelNames.length >= labels.length">
            {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_TARGET_GROUPS.ALL') }}
          </div>
          <div
            v-else-if="!selectedLabelNames.length"
            v-tooltip.top="{
              content: $t('DESIGNS.CONFIGURATION.PRODUCT_GROUPS.18_PLUS_INFO'),
            }"
          >
            <Icon icon="attention" class="attention-icon" />
            {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_TARGET_GROUPS.NONE') }}
          </div>
          <div
            v-else
            v-tooltip="{
              content: selectedLabelNames.join(', '),
            }"
          >
            {{ selectedLabelNames.length }} /
            {{ labels.length }}
          </div>
        </li>
        <li v-if="hasApparelCategory">
          <div>
            <p>
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.HEADING') }}
            </p>
            <p class="text-sm text-grey">
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.SUBHEADING') }}
            </p>
          </div>
          <div v-if="selectedHotspots.length <= 1">
            {{ $t(defaultHotspotName) }}
          </div>
          <div
            v-else
            v-tooltip="{
              content: selectedHotspots.join(', '),
            }"
          >
            {{ `${$t(defaultHotspotName)} +${selectedHotspots.length - 1}` }}
          </div>
        </li>
        <li>
          <div>
            <p>
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.HEADING') }}
            </p>
            <p class="text-sm text-grey">
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.SUBHEADING') }}
            </p>
          </div>
          <div v-if="defaultAppearanceGroup">
            {{ defaultAppearanceGroup.name }}
          </div>
          <div v-else>
            {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.AUTOMATIC') }}
          </div>
        </li>
      </ul>
    </ConfigList>
    <div class="settings-btn-wrapper">
      <button
        class="link-main icon-btn edit-btn"
        @click="emit('openProductSettings')"
      >
        <Icon icon="pencil" />
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.EDIT_CTA') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ConfigList from 'src/app/components/publishing/ConfigList.vue';
import compositionHelper from 'commons/composition/compositionHelper';
import IncrementCounter from 'src/app/components/loadingBar/IncrementCounter.vue';
import { PublishingCategoryConfig } from 'commons/composition/compositionCategoryHelper';
import { HotspotKey, HOTSPOTS } from '@spreadgroup/common-partner-lib/states';
import { getApparelTargetGroupLabels } from '@spreadgroup/common-partner-lib/composition';
import { ExtendedApparelTargetGroupLabel } from '@spreadgroup/common-partner-lib/publishing';
import { i18n } from 'commons/translate/i18n';
import { SmartComposition } from '@spreadgroup/common-partner-lib/composition';
import { useCompositionStore } from 'commons/stores/composition';

const emit = defineEmits<{
  openProductSettings: [];
}>();

const store = useStore();
const compositionStore = useCompositionStore();

const updateTimestamp = ref(Date.now());
const showProgressBar = ref(true);

const { t } = i18n.global;

const composition = computed(
  () => compositionStore.current as SmartComposition,
);

const assortmentCategories = computed(
  () =>
    Object.values(
      store.state.publishingCore.mainCategories,
    ) as PublishingCategoryConfig[],
);
const selectedAssortmentCategoryNames = computed(() => {
  const selectedAssortmentCategories = compositionHelper.getCategories(
    composition?.value,
  ) as PublishingCategoryConfig[];
  return assortmentCategories.value
    .filter((category) =>
      selectedAssortmentCategories.find(
        (selectedCategory) => selectedCategory.id === category.id,
      ),
    )
    .map((c) => c.name);
});
const hasApparelCategory = computed(() =>
  compositionHelper.hasApparelCategory(composition.value),
);

const labels = computed(
  () =>
    Object.values(
      store.state.publishingCore.apparelTargetGroupLabels,
    ) as ExtendedApparelTargetGroupLabel[],
);
const selectedLabelNames = computed(() =>
  getApparelTargetGroupLabels(labels.value, composition.value).map(
    (l) => l.name,
  ),
);

const defaultAppearanceGroup = computed(
  () => store.getters['composition/defaultAppearanceGroup'],
);

const selectedHotspots = computed(() => {
  const hotspotsConfig = compositionHelper.getHotspotConfigs(composition.value);
  return Object.keys(hotspotsConfig).map((key) =>
    t(HOTSPOTS[key as HotspotKey].name),
  );
});

const defaultHotspotName = computed(
  () =>
    HOTSPOTS[composition.value.defaultValues.defaultHotspot as HotspotKey]
      ?.name,
);
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.publishing-properties {
  & > div {
    p {
      margin: 0;
    }
  }

  li > div:last-child {
    text-align: right;
  }
}

.attention-icon {
  margin-right: 8px;
  color: $pa-color-yellow;
}

.settings-btn-wrapper {
  margin-top: 16px;
  border-top: 1px solid $grey10;

  button {
    padding: 16px 0 0 0;
  }
}
</style>
